import {getXPathForField} from "../utils/xpath_minimizer";
import {getElemsByXPath, getIdentityChilds, getIdentityParents, getParents, isParentForElem} from "../utils/utils";


export class Fields {
    document: Document
    fields: Map<string, Field>
    blockXPath: string | null = null

    constructor(document: Document, blockXpath: string | null) {
        this.document = document;
        this.fields = new Map<string, Field>();
        this.blockXPath = blockXpath;
    }

    blockCount(): number {
        if (!this.blockXPath) {
            return 0
        }

        return getElemsByXPath(this.document, this.document.getRootNode() as HTMLElement, this.blockXPath).length
    }
}

export class Field {
    model: Fields
    name: string
    type: string
    htmlClass: string

    // При добавлении элемента храним только его xpath
    // Это позволяет:
    //    1. Легко обобщать xpath
    //    2. Переводить xpath в относительный вид (относительно блока) и в абсолютный (относительно корня документа)
    //    3. И самое важное: в случае, когда мы работаем в режиме обобщённого блока, нам не нужно хранить HTML элементы
    //                       ВСЕХ подобных блоков на странице. При клике на элемент надо только проверить, под какой из
    //                       XPATH попал этот элемент.
    xpath_list: Set<string>

    constructor(model: Fields, name: string, type: string, xpathList?: Set<string>) {
        this.model = model
        this.name = name
        this.type = type
        this.xpath_list = xpathList || new Set<string>()
        this.htmlClass = `pi-field-${this.name}`
    }

    addElem(elem: HTMLElement) {
        let root = this.getParentBlock(elem)
        if (!root) {
            root = this.model.document.getRootNode() as HTMLElement;
        }

        let targetElem = elem;
        if (this.type == "url") {
            for (const el of getIdentityParents(elem, root)) {
                if (el.tagName.toLowerCase() == "a") {
                    targetElem = el
                }
            }

            for (const el of getIdentityChilds(elem)) {
                if (el.tagName.toLowerCase() == "a") {
                    targetElem = el
                }
            }
        }

        let xpath = getXPathForField(targetElem, false, false, root, this.type);

        if (!this.xpath_list.has(xpath)) {
            this.xpath_list.add(xpath);
            this.updateMarks();
        }
    }

    removeElem(elem: HTMLElement) {
        const xpath = this.getCorrespondingXPath(elem);
        if (!xpath) {
            throw new Error(`Cant remove elem from field ${this.name}. Element not found.`)
        }

        if (this.xpath_list.has(xpath)) {
            this.xpath_list.delete(xpath);
            this.updateMarks();
        }
    }

    contains(elem: HTMLElement): boolean {
        return this.getCorrespondingXPath(elem) != null;
    }

    // Ищет конкретную ноду в дереве, которая соответстует xpath `this.blockXPath` (т.е. является блоком) и
    // содержит внутри себя элемент `elem`.
    // Если такого блока не существует, то возвращаем корень документа, т.к. вполне возможно, что в блок данных
    // записываем какойто общий статический контент (например у всех ссылок на странице общая дата публикации/обновления
    // и нам её надо взять ссылки и к каждой добавить дату публикации).
    getParentBlock(elem: HTMLElement): HTMLElement | null {
        if (!this.model.blockXPath) {
            return elem.getRootNode() as HTMLElement
        }

        const blocksElems = getElemsByXPath(this.model.document, this.model.document.getRootNode() as HTMLElement, this.model.blockXPath!);
        for (const block of blocksElems) {
            if (isParentForElem(elem, block)) {
                return block;
            }
        }

        return null;
    }

    // Проверяет, добавлен ли уже этот элемент в список и если добавлен, то возвращает xpath, который соответствует
    // этому элементу.
    getCorrespondingXPath(elem: HTMLElement): string | null {
        for (const xpath of this.xpath_list) {
            let root: HTMLElement | null;
            if (!xpath.startsWith(".")) {
                // Если xpath абсолютный, то ищем от корня
                root = this.model.document.getRootNode() as HTMLElement;
            } else {
                root = this.getParentBlock(elem);
            }

            if (!root) {
                continue
            }

            const elems = new Set(getElemsByXPath(this.model.document, root, xpath));
            if (elems.has(elem)) {
                return xpath;
            }
        }

        return null;
    }

    // Получает все элементы, которые соответствуют этому полю. Если поле было обощено и на странице есть несколько
    // блоков с данными, то будут возвращены элементы всех блоков.
    getElements(): Array<HTMLElement> {
        const globalRoot = this.model.document.getRootNode() as HTMLElement;
        let blockElems: HTMLElement[] = [];
        if (this.model.blockXPath) {
            blockElems = getElemsByXPath(this.model.document, this.model.document.getRootNode() as HTMLElement, this.model.blockXPath)
        }

        const elems = new Array<HTMLElement>();
        for (const xpath of this.xpath_list) {
            if (!xpath.startsWith(".")) {
                // absolute xpath
                for (const elem of getElemsByXPath(this.model.document, globalRoot, xpath)) {
                    elems.push(elem);
                }
            } else {
                // relative xpath
                for (const blockElem of blockElems) {
                    for (const elem of getElemsByXPath(this.model.document, blockElem, xpath)) {
                        elems.push(elem);
                    }
                }
            }
        }

        return elems;
    }

    // Помечает все элементы, которые соответствуют данному полю, html классом. С элементов, которые были ранее помечены,
    // а теперь нет класс будет снят.
    updateMarks() {
        const toUnmark = new Set(this.model.document.getElementsByClassName(this.htmlClass)) as Set<HTMLElement>;

        for (const elem of this.getElements()) {
            toUnmark.delete(elem);

            if (!elem.classList.contains(this.htmlClass)) {
                elem.classList.add(this.htmlClass)
            }
        }

        for (const elem of toUnmark) {
            elem.classList.remove(this.htmlClass);
        }
    }
}
